import {
  FieldArrayPath,
  FieldValues,
  useFieldArray,
  UseFieldArrayProps,
  UseFieldArrayReturn,
  UseFormGetValues,
  UseFormSetValue,
} from 'react-hook-form';

type Props<
  TFieldValues extends FieldValues,
  TRowName extends FieldArrayPath<TFieldValues>,
> = UseFieldArrayProps<TFieldValues, TRowName, 'id'> & {
  // we need to use any because the typing does not work well
  getValues: UseFormGetValues<any>;
  setValue: UseFormSetValue<any>;
};

type ReturnType<
  TFieldValues extends FieldValues,
  TRowName extends FieldArrayPath<TFieldValues>,
> = Omit<UseFieldArrayReturn<TFieldValues, TRowName, 'id'>, 'remove'> & {
  duplicate: (index: number) => void;
  remove: (index: number) => void;
};
// We use this function to reset the predefined dates by passing a prefix name for the field. We need this to reset all the fields that start with the same prefix. For example, if we delete a row, we need to reset all this row's fields that start with the same prefix.
export const useFormArrayUtils = <
  TFormValues extends FieldValues,
  TRowName extends FieldArrayPath<TFormValues>,
>(
  props: Props<TFormValues, TRowName>,
): ReturnType<TFormValues, TRowName> => {
  const { control, name, getValues, setValue } = props;
  const { insert, remove, ...rest } = useFieldArray({
    control,
    name,
  });
  const resetPredefinedDatesRow = (params: { index: number }) => {
    const { index } = params;
    const predefinedDates = getValues('defaultDatesValues');
    const fieldsWithPrefinedDates = Object.keys(predefinedDates);
    const fieldsWithPrefix = fieldsWithPrefinedDates.filter((field) =>
      field.startsWith(`${name}.${index}`),
    );
    fieldsWithPrefix.forEach((field) => {
      delete predefinedDates[field];
    });
    setValue('defaultDatesValues', predefinedDates);
  };

  // We use this function to duplicate the predefined dates by passing a prefix name for the field, the source index, and the destination index.
  const duplicatePredefinedDatesRow = (params: {
    sourceIndex: number;
    destinationIndex: number;
  }) => {
    const { sourceIndex, destinationIndex } = params;
    const predefinedDates = getValues('defaultDatesValues');
    const fieldsWithPrefinedDates = Object.keys(predefinedDates);
    const fieldsWithPrefix = fieldsWithPrefinedDates.filter((field) =>
      field.startsWith(`${name}.${sourceIndex}`),
    );
    fieldsWithPrefix.forEach((field) => {
      const originalValue = predefinedDates[field];
      if (originalValue !== undefined) {
        const newField = field.replace(
          `${name}.${sourceIndex}`,
          `${name}.${destinationIndex}`,
        );
        predefinedDates[newField] = originalValue;
      }
    });
    setValue('defaultDatesValues', predefinedDates);
  };

  // We use this function to reset the edited fields by passing a prefix name for the field. We need this to reset all the fields that start with the same prefix. For example, if we delete a row, we need to reset all this row's fields that start with the same prefix.
  const resetEditedFieldsRow = (params: { index: number }) => {
    const { index } = params;
    const editedFields = getValues('editedFields');
    const editedFieldsWithoutPrefix = editedFields.filter(
      (field: string) => !field.startsWith(`${name}.${index}`),
    );
    setValue('editedFields', editedFieldsWithoutPrefix);
  };

  // We use this function to duplicate the edited fields by passing a prefix name for the field, the source index, and the destination index.
  const duplicateEditedFieldsRow = (params: {
    sourceIndex: number;
    destinationIndex: number;
  }) => {
    const { sourceIndex, destinationIndex } = params;
    const editedFields = getValues('editedFields');
    // Do not filter out destination fields, just add the new ones
    const fieldsToDuplicate = editedFields.filter((field: string) =>
      field.startsWith(`${name}.${sourceIndex}`),
    );
    const newEditedFields = [...editedFields];
    fieldsToDuplicate.forEach((field: string) => {
      const newField = field.replace(
        `${name}.${sourceIndex}`,
        `${name}.${destinationIndex}`,
      );
      if (!newEditedFields.includes(newField)) {
        newEditedFields.push(newField);
      }
    });
    setValue('editedFields', newEditedFields);
  };

  const removeRow = (index: number) => {
    remove(index);
    resetPredefinedDatesRow({ index });
    resetEditedFieldsRow({ index });
  };

  const duplicateRow = (index: number) => {
    const rowToDuplicate = getValues(`${name}.${index}`);
    insert(index, {
      ...rowToDuplicate,
      id: undefined,
    });
    duplicatePredefinedDatesRow({
      sourceIndex: index,
      destinationIndex: index + 1,
    });
    duplicateEditedFieldsRow({
      sourceIndex: index,
      destinationIndex: index + 1,
    });
  };

  return {
    duplicate: duplicateRow,
    remove: removeRow,
    insert,
    ...rest,
  };
};
